/**
 * `<TimeMachineMenu/>` — the "🕰 Time machine" toolbar grouping (#931).
 *
 * The lead toolbar used to list Rewind · Snapshots · Compare as three
 * sibling buttons competing with the view-toggle, the 🔔 bell, and the focal
 * "+ Add". #931 collapses the three TEMPORAL tools into ONE menu button: it
 * opens a compact list of the available time-machine surfaces (each with its
 * existing icon + label + a one-line description), and selecting one opens
 * that overlay exactly as before — the overlays themselves are unchanged.
 *
 * Gating is preserved exactly: each entry is present only when its own
 * component key is on. The PARENT decides which entries to pass (it already
 * resolves each `useTeamFeature` gate); this component just renders whatever
 * entries it's given and never renders an empty menu — the parent omits the
 * whole button when no entry is available, so an all-off team sees no
 * Time-machine affordance at all.
 *
 * Presentational + self-contained: it owns only the open/closed state of its
 * own dropdown and fires the entry's `onSelect`. No data, no gating logic.
 */

import { useEffect, useRef, useState } from "react";

import { PALETTE } from "../tree/palette";

/** One time-machine surface the menu can open. */
export interface TimeMachineEntry {
  /** Stable key (also the test id suffix), e.g. "rewind" / "snapshots" / "compare". */
  key: string;
  /** The entry's existing glyph (⟲ / ◷ / ⇆). */
  icon: string;
  /** The entry's existing label ("Rewind" / "Snapshots" / "Compare"). */
  label: string;
  /** A one-line, calm description of what the surface does. */
  description: string;
  /** Open this surface (the parent wires it to the existing overlay state). */
  onSelect: () => void;
}

export interface TimeMachineMenuProps {
  /** The available entries (already gated by the parent). Never empty here. */
  entries: readonly TimeMachineEntry[];
}

export function TimeMachineMenu({ entries }: TimeMachineMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on an outside click / Esc — the calm dismiss matching the rest of
  // the editor's floating affordances.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        data-testid="time-machine-entry"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-slate-50"
        style={{ borderColor: PALETTE.softBorder, color: PALETTE.slate }}
      >
        🕰 Time machine
      </button>
      {open && (
        <div
          data-testid="time-machine-menu"
          role="menu"
          className="absolute right-0 z-50 mt-1 w-60 overflow-hidden rounded-lg border bg-white py-1 shadow-lg"
          style={{ borderColor: PALETTE.softBorder }}
        >
          {entries.map((entry) => (
            <button
              key={entry.key}
              type="button"
              role="menuitem"
              data-testid={`time-machine-item-${entry.key}`}
              onClick={() => {
                setOpen(false);
                entry.onSelect();
              }}
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-slate-50"
            >
              <span aria-hidden className="mt-0.5 text-sm" style={{ color: PALETTE.teal }}>
                {entry.icon}
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-semibold" style={{ color: PALETTE.ink }}>
                  {entry.label}
                </span>
                <span className="block text-[11px] leading-snug" style={{ color: PALETTE.muted }}>
                  {entry.description}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
